import axios from 'axios'
import { nwAppLog } from './nwLog'

let weightServiceProcess: any = null
let isServiceStarted = false

const WEIGHT_SERVICE_PORT = 48088
const WEIGHT_SERVICE_URL = `http://127.0.0.1:${WEIGHT_SERVICE_PORT}`

const DEFAULT_PATH = 'COM3'
const DEFAULT_BAUD_RATE = 9600
const DEFAULT_DATA_BITS = 8
const DEFAULT_STOP_BITS = 1
const DEFAULT_PARITY = 'none'

let path = !process.server && localStorage.getItem('nwWeightPath') || DEFAULT_PATH
let baudRate = !process.server && Number(localStorage.getItem('nwWeightBaudRate')) || DEFAULT_BAUD_RATE
let dataBits = !process.server && Number(localStorage.getItem('nwWeightDataBits')) || DEFAULT_DATA_BITS
let stopBits = !process.server && Number(localStorage.getItem('nwWeightStopBits')) || DEFAULT_STOP_BITS
let parity = !process.server && localStorage.getItem('nwWeightParity') || DEFAULT_PARITY

// 启动称重服务子进程
function startWeightService(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (!window.require) {
        nwAppLog('当前环境不支持子进程调用')
        resolve(false)
        return
      }

      const { spawn } = window.require('child_process')
      const path = window.require('path')
      const fs = window.require('fs')

      // 确定可执行文件路径和名称
      const platform = process.platform
      let executableName = `weight-service-${process.arch}`
      if (platform === 'win32') {
        executableName = 'weight-service.exe'
      }

      const executablePath = path.join(nw.App.startPath, 'tools/weight_tools/', executableName)

      // 检查可执行文件是否存在
      if (!fs.existsSync(executablePath)) {
        console.error(`称重服务可执行文件不存在: ${executablePath}`)
        resolve(false)
        return
      }

      // 启动可执行文件
      weightServiceProcess = spawn(executablePath, [WEIGHT_SERVICE_PORT.toString()], {
        stdio: ['pipe', 'pipe', 'pipe'],
      })

      weightServiceProcess.stdout.on('data', (data: Buffer) => {
        const output = data.toString()
        console.log('称重服务输出:', output)
        if (output.includes('称重服务已启动')) {
          isServiceStarted = true
          resolve(true)
        }
      })

      weightServiceProcess.stderr.on('data', (data: Buffer) => {
        console.error('称重服务错误:', data.toString())
      })

      weightServiceProcess.on('close', (code: number) => {
        console.error(`称重服务进程退出，退出码: ${code}`)
        isServiceStarted = false
        weightServiceProcess = null
      })

      weightServiceProcess.on('error', (error: Error) => {
        console.error('启动称重服务失败:', error.message)
        resolve(false)
      })

      // 设置超时
      setTimeout(() => {
        if (!isServiceStarted) {
          resolve(false)
        }
      }, 5000)
    }
    catch (error: any) {
      console.error('启动称重服务异常:', error.message)
      resolve(false)
    }
  })
}

// 停止称重服务
export function stopWeightService() {
  if (weightServiceProcess) {
    weightServiceProcess.kill()
    weightServiceProcess = null
    isServiceStarted = false
  }
}

// 初始化称重功能
export async function initNwWeight(): Promise<boolean> {
  try {
    // 启动服务
    const started = await startWeightService()
    if (!started) {
      return false
    }

    // 等待服务完全启动
    await new Promise(resolve => setTimeout(resolve, 1000))

    // 配置串口参数
    await axios(`${WEIGHT_SERVICE_URL}/config`, {
      method: 'POST',
      data: {
        path,
        baudRate,
        dataBits,
        stopBits,
        parity,
      },
    })

    // 启动串口监听
    await axios(`${WEIGHT_SERVICE_URL}/start`, {
      method: 'POST',
    })

    return true
  }
  catch (error: any) {
    console.error('初始化称重服务失败:', error.message)
    return false
  }
}

// 获取最新重量
export async function getLatestWeight(): Promise<{ value: number, unit: string } | null> {
  try {
    if (!isServiceStarted) {
      return null
    }

    const res: any = await axios(`${WEIGHT_SERVICE_URL}/weight`)
    return res.data.data as { value: number, unit: string }
  }
  catch (error: any) {
    console.error('获取重量失败:', error.message)
    return null
  }
}

// 设置称重配置
export async function setNwWeightConfig(config: {
  path: string
  baudRate: number
  dataBits: number
  stopBits?: number
  parity?: string
}): Promise<boolean> {
  try {
    path = config.path
    baudRate = config.baudRate || DEFAULT_BAUD_RATE
    dataBits = config.dataBits || DEFAULT_DATA_BITS
    stopBits = config.stopBits || DEFAULT_STOP_BITS
    parity = config.parity || DEFAULT_PARITY

    localStorage.setItem('nwWeightPath', path)
    localStorage.setItem('nwWeightBaudRate', baudRate.toString())
    localStorage.setItem('nwWeightDataBits', dataBits.toString())
    localStorage.setItem('nwWeightStopBits', stopBits.toString())
    localStorage.setItem('nwWeightParity', parity)

    if (isServiceStarted) {
      await axios(`${WEIGHT_SERVICE_URL}/config`, {
        method: 'POST',
        data: {
          path,
          baudRate,
          dataBits,
          stopBits,
          parity,
        },
      })
    }

    return true
  }
  catch (error: any) {
    console.error('设置称重配置失败:', error.message)
    return false
  }
}

// 获取称重配置
export function getNwWeightConfig() {
  return {
    path,
    baudRate,
    dataBits,
    stopBits,
    parity,
  }
}

// 测试称重连接
export async function testWeightConnection(config: {
  path: string
  baudRate: number
  dataBits: number
  stopBits?: number
  parity?: string
}): Promise<{ success: boolean, message: string }> {
  try {
    if (!isServiceStarted) {
      return { success: false, message: '称重服务未启动' }
    }

    const res = await axios(`${WEIGHT_SERVICE_URL}/test`, {
      method: 'POST',
      data: {
        path: config.path,
        baudRate: config.baudRate,
        dataBits: config.dataBits,
        stopBits: config.stopBits || 1,
        parity: config.parity || 'none',
      },
    })

    return res.data as { success: boolean, message: string }
  }
  catch (error: any) {
    return { success: false, message: error.message }
  }
}

export function stopNwWeight() {
  stopWeightService()
}
if (!process.server) {
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      stopWeightService()
    })
  }

  if (typeof nw !== 'undefined') {
    nw.Window.get(window).on('close', () => {
      stopWeightService()
    })
  }
}

